/**
 * stainedGlassPipeline — circular stained-glass window pattern.
 *
 * A circular window divided into concentric rings and radial slices.
 * Each "pane" of glass has a distinct color (warm reds, blues,
 * yellows, purples). The glass is translucent — light shines
 * through from behind, brightening near the center.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface StainedGlassPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);

  // Polar coords.
  float r = length(p);
  float theta = atan(p.y, p.x);

  // Circular window shape.
  float windowRadius = 0.55 + bass * 0.05;
  float inWindow = smoothstep(windowRadius, windowRadius - 0.005, r);

  // Concentric rings: divide r into segments.
  float ringIdx = floor(r * 8.0);
  // Radial slices: divide theta into segments.
  float sliceIdx = floor((theta + 3.14159265) * 6.0 / 3.14159265);
  // Pane ID.
  vec2 paneId = vec2(ringIdx, sliceIdx);
  float h = hash21(paneId);
  // Stained glass colors: warm palette with rich variation.
  vec3 c1 = vec3(0.85, 0.15, 0.10);  // crimson
  vec3 c2 = vec3(0.95, 0.65, 0.10);  // gold
  vec3 c3 = vec3(0.20, 0.45, 0.95);  // royal blue
  vec3 c4 = vec3(0.65, 0.20, 0.85);  // purple
  vec3 c5 = vec3(0.10, 0.75, 0.65);  // emerald
  vec3 c6 = vec3(0.95, 0.40, 0.65);  // pink
  vec3 cellColor;
  float hBucket = fract(h * 6.0);
  if (hBucket < 1.0 / 6.0) cellColor = c1;
  else if (hBucket < 2.0 / 6.0) cellColor = c2;
  else if (hBucket < 3.0 / 6.0) cellColor = c3;
  else if (hBucket < 4.0 / 6.0) cellColor = c4;
  else if (hBucket < 5.0 / 6.0) cellColor = c5;
  else cellColor = c6;

  // Light shining through: brighter at the center, fading outward.
  float lightIntensity = exp(-r * 1.5) * 0.8;
  // Audio: pulse with bass.
  lightIntensity *= 1.0 + bass * 0.5;
  // Per-cell variation: some cells brighter than others.
  lightIntensity *= 0.7 + 0.3 * hash21(paneId + 7.0);

  // Glass color: modulated by light passing through.
  vec3 glassColor = cellColor * (0.4 + lightIntensity);

  // Bright edges between cells (lead "came").
  vec2 cellLocal = vec2(fract(r * 8.0), fract((theta + 3.14159265) * 6.0 / 3.14159265));
  float edge = step(0.96, max(cellLocal.x, cellLocal.y));
  glassColor = mix(glassColor, vec3(0.15, 0.10, 0.05), edge * 0.8);

  // Dark background outside the window.
  vec3 bg = vec3(0.04, 0.04, 0.06);
  vec3 col = mix(bg, glassColor, inWindow);

  // Inner highlight (audio: brightens with high).
  col += vec3(1.0, 0.95, 0.85) * exp(-r * 6.0) * inWindow * high * 0.4;

  // Vignette.
  float vig = 1.0 - dot(p, p) * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeStainedGlassBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeStainedGlassRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createStainedGlassPipeline(gl: WebGL2RenderingContext): StainedGlassPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawStainedGlass(
  gl: WebGL2RenderingContext,
  pipe: StainedGlassPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
